import dayjs, { Dayjs } from "dayjs";

import { buildMonthlyCashflowAmounts, MonthlyCashflowAmounts, roundMonthlyCashflow } from "@/logics/thisMonthCashflow";
import { PropertyHolder } from "@/view_models/atoms";
import { MonthlyBalanceSheet } from "@/view_models/balancesheetTypes";

export type BalancePreset = "month" | "previous" | "year" | "custom";
export type PropertyMonthMaps = Map<number, Map<string, MonthlyBalanceSheet>>;
export const balancePeriod = (preset: Exclude<BalancePreset, "custom">, now = dayjs()) => {
  const target = preset === "previous" ? now.subtract(1, "month") : now;
  return {
    start: target.startOf(preset === "year" ? "year" : "month"),
    end: target.endOf(preset === "year" ? "year" : "month"),
  };
};
export const validBalanceRange = (start: unknown, end: unknown) => {
  if (start == null || end == null) return false;
  const first = dayjs(start as never);
  const last = dayjs(end as never);
  return first.isValid() && last.isValid() && !last.startOf("month").isBefore(first.startOf("month"));
};

const monthAmounts = (properties: PropertyHolder[], maps: PropertyMonthMaps, month: Dayjs) => {
  const sums = buildMonthlyCashflowAmounts(undefined, {});
  for (const holder of properties) {
    const sheet = maps.get(holder.id)?.get(month.format("YYYY/MM"));
    if (!sheet) {
      const bought = dayjs(holder.property.bought_at);
      // 購入前にはその物件の計算結果がない。保有開始後の欠損とは区別する。
      if (holder.property.bought_at && bought.isValid() && month.isBefore(bought, "month")) continue;
      return null;
    }
    const amounts = buildMonthlyCashflowAmounts(holder.property.property_category, sheet.result);
    (Object.keys(sums) as Array<keyof MonthlyCashflowAmounts>).forEach((key) => {
      sums[key] += amounts[key];
    });
  }
  return sums;
};

export const buildBalanceSummary = (
  allProperties: PropertyHolder[],
  selectedIds: number[],
  maps: PropertyMonthMaps,
  start: Dayjs,
  end: Dayjs,
  now = dayjs()
) => {
  const properties = allProperties.filter((holder) => selectedIds.includes(holder.id));
  const sums = buildMonthlyCashflowAmounts(undefined, {});
  let available = validBalanceRange(start, end);
  const count = available ? end.startOf("month").diff(start.startOf("month"), "month") + 1 : 0;
  for (let index = 0; index < count; index++) {
    const amounts = monthAmounts(properties, maps, start.add(index, "month"));
    if (!amounts) {
      available = false;
      continue;
    }
    (Object.keys(sums) as Array<keyof MonthlyCashflowAmounts>).forEach((key) => {
      sums[key] += amounts[key];
    });
  }
  const graphEnd = end.isAfter(now, "month") ? now.startOf("month") : end.startOf("month");
  const trend = Array.from({ length: 12 }, (_, index) => {
    const month = graphEnd.subtract(11 - index, "month");
    const amounts = monthAmounts(properties, maps, month);
    return { month: month.format("YYYY/MM"), amount: amounts ? roundMonthlyCashflow(amounts).totalCashFlow : null };
  });
  const previous = monthAmounts(properties, maps, start.subtract(1, "month"));
  const rounded = roundMonthlyCashflow(sums);
  const perProperty = allProperties.map((holder) => {
    const entry = buildMonthlyCashflowAmounts(undefined, {});
    let hasValue = true;
    for (let index = 0; index < count; index++) {
      const amounts = monthAmounts([holder], maps, start.add(index, "month"));
      if (!amounts) {
        hasValue = false;
        continue;
      }
      (Object.keys(entry) as Array<keyof MonthlyCashflowAmounts>).forEach((key) => {
        entry[key] += amounts[key];
      });
    }
    return { property: holder, amount: hasValue ? roundMonthlyCashflow(entry).totalCashFlow : null };
  });
  let loanRemaining = 0;
  let loanAvailable = true;
  properties.forEach((holder) => {
    const sheet = maps.get(holder.id)?.get(now.format("YYYY/MM"));
    if (!sheet || !Number.isFinite(sheet.remaining_loan_amount)) loanAvailable = false;
    else loanRemaining += Math.max(0, sheet.remaining_loan_amount);
  });
  const loanOriginal = properties.reduce((sum, holder) => sum + (holder.property.loan_amount || 0), 0);
  return {
    amounts: sums,
    ...rounded,
    available,
    delta:
      count === 1 && previous && available
        ? rounded.totalCashFlow - roundMonthlyCashflow(previous).totalCashFlow
        : null,
    trend,
    graphEnd,
    perProperty,
    // ホームと同じ購入価格合計・当月残債。選択期間中の購入/売却金額へ置き換えない。
    assets: Math.round(properties.reduce((sum, holder) => sum + holder.property.total_price_of_property, 0)),
    loanRemaining: loanAvailable ? Math.round(loanRemaining) : null,
    loanProgress:
      loanAvailable && loanOriginal > 0 ? Math.max(0, Math.min(100, (1 - loanRemaining / loanOriginal) * 100)) : null,
    includesSample: properties.some((holder) => holder.property.property_type === 3),
    hasBuilding: properties.some((holder) => holder.property.property_category === "一棟物件"),
  };
};

export type BalanceSummaryData = ReturnType<typeof buildBalanceSummary>;
